// @file Pinia Store for the Library accept invite page
import getCsrfToken from '@@/bits/csrf_token'
import currentUser from '@@/bits/current_user'
import { clearDashViewUrlFromStorage, dashMakeUrl } from '@@/bits/dash_helper'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { buildUrlFromPath, navigateTo, transformUrl } from '@@/bits/location'
import { HttpCode, LibraryMembershipRole, LibraryType } from '@@/enums'
import type { Library, LibraryMembership, User } from '@@/types'
import { MobilePage } from '@@/types/dash'
import type { JsonAPIResponse } from '@padlet/arvo/src'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// Refer to vue_starting_state in auth/libraries/accept_invite_controller#index
interface VueStartingStateLibraryAcceptInvite {
  user: User
  library: Library
  slug: string
  role: LibraryMembershipRole
  code: string
  hasAcceptedInvite: boolean
  isActiveLibraryMember: boolean
  isDeactivatedLibraryMember: boolean
  isUserLoggedIn: boolean
  loggedInBefore: boolean
  exceededQuota: boolean
  email?: string
  emailHasExistingUser?: boolean
  redirectUrl?: string
  disableAlreadyEnrolledAlert?: boolean
  isReseller?: boolean
  inviteRejectionEnabled?: boolean
}

// This page is reachable while logged out, so it must not instantiate the dash navigation store.
// That store's setup body prerenders the current library's settings page, and a speculative request
// for an authenticated URL redirects through /auth/login, whose `referrer` param overwrites the
// post-login destination this page just set. Build the make URL from the pure helper instead.
const useLibraryAcceptInviteStore = defineStore('libraryAcceptInvite', () => {
  // State
  const user = ref<User>(currentUser)
  const library = ref<Partial<Library>>({})
  const slug = ref<string>('')
  const role = ref<LibraryMembershipRole>(LibraryMembershipRole.Guest)
  const code = ref<string>('')
  const isActiveLibraryMember = ref<boolean>(false)
  const isDeactivatedLibraryMember = ref<boolean>(false)
  const hasAcceptedInvite = ref<boolean>(false)
  const isAwaitingServerResponse = ref<boolean>(false)
  const errorMessage = ref<string>('')
  const isUserLoggedIn = ref<boolean>(false)
  const loggedInBefore = ref<boolean>(false)
  const exceededQuota = ref<boolean>(false)
  const email = ref<string | undefined>(undefined)
  const emailHasExistingUser = ref<boolean>(false)
  const disableAlreadyEnrolledAlert = ref<boolean>(false)
  const isReseller = ref<boolean>(false)
  const inviteRejectionEnabled = ref<boolean>(false)
  const redirectUrl = ref<string | undefined>(undefined)

  // Getters
  const isClassroomLibrary = computed<boolean>(() => {
    return library.value.libraryType === LibraryType.Classroom
  })
  const isSchoolLibrary = computed<boolean>(() => {
    return library.value.libraryType === LibraryType.School
  })
  const canUserAccessGettingStartedTab = computed((): boolean => {
    // Only School Admins can access the Getting Started tab,
    // Classroom and Team Admins cannot.
    //
    // School, Classroom and Team Owners can access the Getting Started tab,
    // but they won't go through this Accept Invite flow since they're already a member of the library.
    return isSchoolLibrary.value && role.value === LibraryMembershipRole.Admin
  })
  const goToLogin = computed<boolean>(() => {
    return emailHasExistingUser.value || (email.value === undefined && loggedInBefore.value)
  })

  // Actions
  function initializeState(startingState: VueStartingStateLibraryAcceptInvite): void {
    user.value = startingState.user
    library.value = startingState.library
    slug.value = startingState.slug
    role.value = startingState.role
    code.value = startingState.code
    hasAcceptedInvite.value = startingState.hasAcceptedInvite
    isActiveLibraryMember.value = startingState.isActiveLibraryMember
    isDeactivatedLibraryMember.value = startingState.isDeactivatedLibraryMember
    isUserLoggedIn.value = startingState.isUserLoggedIn
    exceededQuota.value = startingState.exceededQuota
    email.value = startingState.email
    emailHasExistingUser.value = startingState.emailHasExistingUser ?? emailHasExistingUser.value
    loggedInBefore.value = startingState.loggedInBefore ?? loggedInBefore.value
    disableAlreadyEnrolledAlert.value = startingState.disableAlreadyEnrolledAlert ?? disableAlreadyEnrolledAlert.value
    isReseller.value = startingState.isReseller ?? isReseller.value
    inviteRejectionEnabled.value = startingState.inviteRejectionEnabled ?? inviteRejectionEnabled.value
    redirectUrl.value = startingState.redirectUrl ?? redirectUrl.value

    if (
      startingState.isActiveLibraryMember &&
      startingState.redirectUrl !== undefined &&
      disableAlreadyEnrolledAlert.value
    ) {
      const alreadyEnrolledRedirectUrl = startingState.redirectUrl
      isAwaitingServerResponse.value = true
      setTimeout(() => {
        navigateTo(alreadyEnrolledRedirectUrl ?? '/')
      }, 2000)
    }
  }

  function goToLibraryDashboardGettingStartedTab(): void {
    if (library.value.dashboardUrl === undefined) {
      errorMessage.value = __('Something went wrong. Please try again later.')
      return
    }

    const libraryDashboardGettingStartedTabUrl = transformUrl(buildUrlFromPath(library.value.dashboardUrl), {
      searchParams: {
        page: MobilePage.GettingStarted,
      },
    })

    clearDashViewUrlFromStorage() // if not there will be a redirect from libraryDashboardUrl to the dashViewUrlFromStorage
    navigateTo(libraryDashboardGettingStartedTabUrl)
  }

  function goToLibraryDashboardOrMakePage(): void {
    if (library.value.dashboardUrl === undefined) {
      errorMessage.value = __('Something went wrong. Please try again later.')
      return
    }

    navigateTo(dashMakeUrl(library.value.dashboardUrl))
  }

  const isAwaitingRejection = ref<boolean>(false)

  async function rejectInvite(): Promise<void> {
    try {
      isAwaitingRejection.value = true
      await fetchJson('/api/1/libraries/reject-invite', {
        method: HTTPMethod.post,
        csrfToken: getCsrfToken(),
        jsonData: {
          data: {
            attributes: {
              libraryId: library.value.id,
            },
          },
        },
      })
    } catch (e) {
      captureFetchException(e, { source: 'LibraryAcceptInviteRejectInvite' })
      throw e
    } finally {
      isAwaitingRejection.value = false
    }
  }

  async function acceptInvite(): Promise<void> {
    try {
      isAwaitingServerResponse.value = true
      const response = await fetchJson<LibraryMembership>('/api/1/libraries/accept_invite', {
        method: HTTPMethod.post,
        csrfToken: getCsrfToken(),
        jsonData: {
          data: {
            attributes: {
              libraryId: library.value.id,
              userId: user.value.id,
              role: role.value,
              code: code.value,
            },
          },
        },
      })
      const nextUrl = (response as JsonAPIResponse<LibraryMembership>).meta?.nextUrl
      if (nextUrl != null) {
        navigateTo(nextUrl)
      } else if (redirectUrl.value != null && redirectUrl.value !== '') {
        navigateTo(redirectUrl.value)
      } else if (canUserAccessGettingStartedTab.value) {
        goToLibraryDashboardGettingStartedTab()
      } else {
        goToLibraryDashboardOrMakePage()
      }
    } catch (e) {
      isAwaitingServerResponse.value = false

      if (
        [
          HttpCode.Unauthorized,
          HttpCode.Forbidden,
          HttpCode.NotFound,
          HttpCode.UnprocessableEntity,
          HttpCode.ServerError,
        ].includes(e.status)
      ) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const error = parsedErrorMessage.errors[0]

          errorMessage.value = error.title
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else {
        captureFetchException(e, { source: 'LibraryAcceptInviteAcceptInvite' })
      }
    }
  }

  return {
    // State
    user,
    role,
    slug,
    isActiveLibraryMember,
    isDeactivatedLibraryMember,
    code,
    hasAcceptedInvite,
    isAwaitingServerResponse,
    isAwaitingRejection,
    errorMessage,
    isUserLoggedIn,
    loggedInBefore,
    exceededQuota,
    library,
    email,
    disableAlreadyEnrolledAlert,
    isReseller,
    inviteRejectionEnabled,
    redirectUrl,

    // Getters
    isClassroomLibrary,
    isSchoolLibrary,
    goToLogin,

    // Actions
    initializeState,
    goToLibraryDashboardOrMakePage,
    acceptInvite,
    rejectInvite,
  }
})
export { useLibraryAcceptInviteStore }
export type { VueStartingStateLibraryAcceptInvite }
